import { useState, useEffect, useCallback } from 'react';
import { DailyEnvironmentalDataService } from '../../../manage/services/dailyEnvironmentalData.service';
import type { DailyEnvironmentalData } from '../../../../types/api';

export interface UseDailyEnvironmentalDataReturn {
  todayEnvironmentalData: DailyEnvironmentalData | null;
  isLoadingEnvironmentalData: boolean;
  environmentalDataMissing: boolean;
  refetch: () => void;
}

/**
 * Fetches today's admin-configured environmental reading (ambient temp,
 * humidity, atm. pressure, weather) once when the modal opens. The backend injects
 * this into the calibration server-side and blocks creation if it's
 * missing — this hook exists so the UI can show/gate accordingly before
 * the user gets that far.
 */
export const useDailyEnvironmentalData = (isOpen: boolean): UseDailyEnvironmentalDataReturn => {
  const [todayEnvironmentalData, setTodayEnvironmentalData] = useState<DailyEnvironmentalData | null>(null);
  const [isLoadingEnvironmentalData, setIsLoadingEnvironmentalData] = useState(false);
  const [environmentalDataMissing, setEnvironmentalDataMissing] = useState(false);

  const fetchToday = useCallback(async () => {
    setIsLoadingEnvironmentalData(true);
    try {
      const data = await DailyEnvironmentalDataService.getToday();
      setTodayEnvironmentalData(data);
      setEnvironmentalDataMissing(false);
    } catch (error: any) {
      setTodayEnvironmentalData(null);
      // 404 means no admin has entered today's data yet — a real, expected state.
      setEnvironmentalDataMissing(true);
    } finally {
      setIsLoadingEnvironmentalData(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchToday();
    }
  }, [isOpen, fetchToday]);

  return {
    todayEnvironmentalData,
    isLoadingEnvironmentalData,
    environmentalDataMissing,
    refetch: fetchToday,
  };
};
